import { useMemo, useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'

import { Container } from 'components/container'
import PhotoPlaceholder from 'components/assets/nophoto.svg'
import { useAppContext } from 'context/app-context'
import { Person } from 'models'
import { resizeGdbImage } from 'utils'
import { TextWithLinks } from './helpers'
import { Pagination } from './pagination'
import {
  Additional,
  CloseButton,
  Dl,
  Header,
  Layout,
  Main,
  Modal,
  Name,
  ProfilePicture,
  Row,
  SwiperContainer,
  ButtonMore,
  Magnify,
  ListWrapper,
} from './styled'

export const Detail = () => {
  const { data, strings } = useAppContext()
  const { id } = useParams()
  const [openPhotoUrl, setOpenPhotoUrl] = useState('')
  const person = useMemo(
    () => data.find(d => d.id.toString() === id) as Person | undefined,
    [data, id]
  )
  const refAdditional = useRef<HTMLDivElement>(null)
  const [additionalButton, setAdditionalButton] = useState(false)
  const [additionalOpen, setAdditionalOpen] = useState(false)

  useEffect(() => {
    document.body.classList.add('white')
  }, [])

  useEffect(() => {
    setAdditionalOpen(false)
    if (refAdditional.current !== null) {
      if (window.innerWidth >= 1360) {
        if (refAdditional.current.clientHeight > 61) {
          setAdditionalButton(true)
        } else {
          setAdditionalButton(false)
        }
      } else {
        if (refAdditional.current.clientHeight > 45) {
          setAdditionalButton(true)
        } else {
          setAdditionalButton(false)
        }
      }
    }
  }, [id])

  if (!person) {
    return (
      <Container>
        <p>Not found.</p>
      </Container>
    )
  }

  return (
    <Container>
      <Layout>
        <Header>
          <Pagination id={person.id} />
          <div>
            <Name>{person.name}</Name>
            <ProfilePicture
              src={resizeGdbImage(
                person.main_photo_url || PhotoPlaceholder,
                640,
                undefined,
                { _r: 6 }
              )}
              alt={person.name}
            />
          </div>
        </Header>

        <Main>
          <ListWrapper>
            <Dl>
              <Row>
                <dt>{strings.date_of_birth}</dt>
                <dd>{person.date_of_birth}</dd>
              </Row>
              <Row>
                <dt>{strings.date_of_death}</dt>
                <dd>{person.date_of_death}</dd>
              </Row>
              <Row>
                <dt>{strings.citizenship}</dt>
                <dd>{person.citizenship}</dd>
              </Row>
              <Row>
                <dt>{strings.place_of_origin}</dt>
                <dd>{person.place_of_origin}</dd>
              </Row>
            </Dl>
            <Dl>
              <Row>
                <dt>{strings.place_of_death}</dt>
                <dd>{person.place_of_death}</dd>
              </Row>
              <Row>
                <dt>{strings.burial_place}</dt>
                <dd>{person.burial_place}</dd>
              </Row>
              <Row>
                <dt>{strings.formation_of_the_raf}</dt>
                <dd>{person.formation_of_the_raf}</dd>
              </Row>
              <Row>
                <dt>{strings.military}</dt>
                <dd>{person.military}</dd>
              </Row>
            </Dl>
          </ListWrapper>
          <Dl>
            <Row>
              <dt>{strings.info_source}</dt>
              <dd>
                <TextWithLinks text={person.info_source} />
              </dd>
            </Row>

            {!!person.additional_info && (
              <>
                {<h3>{strings.additional_info}</h3>}
                <Additional $opened={additionalOpen} ref={refAdditional}>
                  <TextWithLinks text={person.additional_info} />
                </Additional>
                {additionalButton && (
                  <ButtonMore
                    $up={additionalOpen}
                    onClick={() => setAdditionalOpen(!additionalOpen)}
                  >
                    {additionalOpen ? strings.see_less : strings.see_more}
                  </ButtonMore>
                )}
              </>
            )}
          </Dl>

          {!!person?.additional_photos && (
            <SwiperContainer>
              <div className="swiper-button-prev" />
              <Swiper
                modules={[Navigation]}
                slidesPerView={1}
                navigation={{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }}
              >
                {person?.additional_photos.split(',').map((photoUrl, i) => (
                  <SwiperSlide key={i}>
                    <div onClick={() => setOpenPhotoUrl(photoUrl.trim())}>
                      <img src={resizeGdbImage(photoUrl.trim(), 640)} alt="" />

                      <Magnify />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              <div className="swiper-button-next" />
            </SwiperContainer>
          )}

          {!!openPhotoUrl && (
            <Modal>
              <img src={openPhotoUrl} alt="" />
              <CloseButton onClick={() => setOpenPhotoUrl('')} />
            </Modal>
          )}
        </Main>
      </Layout>
    </Container>
  )
}
